// utils/notificationUtils.js

// Check if user should receive notification based on settings
export const shouldSendNotification = (notificationType = 'default') => {
    // Check if browser supports notifications
    if (!('Notification' in window)) return false;
    
    // Check permission
    if (Notification.permission !== 'granted') return false;
    
    // Check user settings
    const settings = getNotificationSettings();
    if (!settings.enabled) return false;
    
    // Check quiet hours
    if (settings.quietHours) {
      const now = new Date();
      const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      
      if (currentTime >= settings.quietStart && currentTime <= settings.quietEnd) {
        return false;
      }
    }
    
    return true;
  };
  
  // Get notification settings from localStorage
  export const getNotificationSettings = () => {
    const defaultSettings = {
      enabled: true,
      sound: true,
      desktop: true,
      email: false,
      quietHours: false,
      quietStart: '22:00',
      quietEnd: '08:00'
    };
    
    const saved = localStorage.getItem('notification_settings');
    if (saved) {
      try {
        return { ...defaultSettings, ...JSON.parse(saved) };
      } catch (e) {
        return defaultSettings;
      }
    }
    
    return defaultSettings;
  };
  
  // Show notification with settings check
  export const showNotification = (title, options = {}) => {
    if (!shouldSendNotification(options.type)) return;
    
    const settings = getNotificationSettings();
    
    // Create notification
    const notification = new Notification(title, {
      body: options.body,
      icon: options.icon || '/favicon.ico',
      tag: options.tag,
      requireInteraction: options.requireInteraction || false,
      silent: !settings.sound
    });
    
    // Handle click
    if (options.onClick) {
      notification.onclick = () => {
        window.focus();
        options.onClick();
        notification.close();
      };
    }
    
    // Auto close after 5 seconds
    setTimeout(() => notification.close(), 5000);
    
    // Play sound if enabled
    if (settings.sound && options.type) {
      playNotificationSound(options.type);
    }
    
    return notification;
  };
  
  // Play notification sound
  export const playNotificationSound = (type = 'default') => {
    const settings = getNotificationSettings();
    if (!settings.sound) return;
    
    const sounds = {
      order: '/sounds/order.mp3',
      message: '/sounds/message.mp3',
      favorite: '/sounds/favorite.mp3',
      boost: '/sounds/boost.mp3',
      payment: '/sounds/payment.mp3',
      dispute: '/sounds/dispute.mp3',
      default: '/sounds/notification.mp3'
    };
    
    const soundUrl = sounds[type] || sounds.default;
    
    try {
      const audio = new Audio(soundUrl);
      audio.volume = 0.5;
      audio.play().catch(e => console.log('Sound play failed:', e));
    } catch (error) {
      console.error('Error playing sound:', error);
    }
  };
  
  // Request permission with user-friendly prompt
  export const requestNotificationPermission = async (showPrompt = true) => {
    if (!('Notification' in window)) {
      console.warn('Notifications not supported');
      return false;
    }
    
    if (Notification.permission === 'granted') {
      return true;
    }
    
    if (Notification.permission === 'denied') {
      if (showPrompt) {
        alert('Notifications are blocked. Please enable them in your browser settings to receive real-time updates.');
      }
      return false;
    }
    
    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting permission:', error);
      return false;
    }
  };
  
  // Initialize notifications on page load
  export const initNotifications = () => {
    // Check if already granted
    if (Notification.permission === 'granted') {
      console.log('✅ Notifications already enabled');
      return true;
    }
    
    // Check if user has dismissed prompt
    const dismissed = localStorage.getItem('notification_prompt_dismissed');
    if (dismissed === 'true') {
      return false;
    }
    
    // Wait for user interaction (optional)
    const handleUserInteraction = () => {
      setTimeout(() => {
        if (Notification.permission === 'default') {
          // Show gentle prompt after 3 seconds of inactivity
          const prompt = document.createElement('div');
          prompt.className = 'fixed bottom-4 right-4 z-50 animate-slide-up';
          prompt.innerHTML = `
            <div class="bg-white rounded-xl shadow-xl border p-4 max-w-sm">
              <div class="flex gap-3">
                <div class="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <svg class="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                  </svg>
                </div>
                <div class="flex-1">
                  <h4 class="font-semibold text-gray-900">Enable Notifications</h4>
                  <p class="text-sm text-gray-600 mt-1">Get real-time alerts for orders, messages, and updates</p>
                  <div class="flex gap-2 mt-3">
                    <button id="enable-notifications" class="px-3 py-1.5 bg-orange-500 text-white text-sm rounded-lg">Enable</button>
                    <button id="dismiss-notifications" class="px-3 py-1.5 text-gray-600 text-sm rounded-lg">Maybe Later</button>
                  </div>
                </div>
                <button id="close-notifications" class="text-gray-400 hover:text-gray-600">✕</button>
              </div>
            </div>
          `;
          document.body.appendChild(prompt);
          
          // Add event listeners
          document.getElementById('enable-notifications')?.addEventListener('click', async () => {
            await requestNotificationPermission(true);
            prompt.remove();
          });
          
          document.getElementById('dismiss-notifications')?.addEventListener('click', () => {
            localStorage.setItem('notification_prompt_dismissed', 'true');
            prompt.remove();
          });
          
          document.getElementById('close-notifications')?.addEventListener('click', () => {
            prompt.remove();
          });
        }
      }, 3000);
      
      // Remove event listeners
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('scroll', handleUserInteraction);
    };
    
    // Wait for first user interaction
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('scroll', handleUserInteraction, { once: true });
    
    return false;
  };